import { Typography, Box, Stack, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IBreadcrumbsAndMenu,
  IExporter,
  IMenuOption,
} from "../../../types/global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import ContactTable from "../../../components/Table/Contact";
import { useModal } from "../../../hooks/use-modal";
import ExporterModal from "../../../components/UI/Modal/ExporterModal";
import CustomizedMenuOptions from "../../../components/Custom/CustomizedMenuOptions";
import {
  ContactReportType,
  GenerateContactReportQuery,
  ReportGenerateInput,
  useGenerateContactReportQuery,
} from "../../../generated/contact";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { customerTypeOptions } from "../../../utils/Formatter/Contact";

const customerOptions: IMenuOption[] = [
  {
    value: "นำเข้าข้อมูล",
  },
  {
    value: "นำออกข้อมูล",
  },
];

const Customer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("contact.index"),
      to: "/contact",
    },
    {
      name: t("contact.contact_type.customer"),
    },
  ];

  const { modal, openModalHandler, closeModalHandler } = useModal();

  const graphqlClient = createGraphQLClientWithMiddleware("crm");

  const [exportInput, setExportInput] = useState<ReportGenerateInput | null>(
    null
  );

  const { refetch: refetchData, isLoading: isExporting } =
    useGenerateContactReportQuery<GenerateContactReportQuery>(
      graphqlClient,
      {
        input: exportInput,
      },
      {
        enabled: false,
      }
    );

  const exportHandler = async (data: IExporter) => {
    if (data) {
      const { type, start_date, end_date } = data;
      setExportInput({
        report_type: type as ContactReportType,
        start_date: start_date,
        end_date: end_date,
      });
    }
  };

  useEffect(() => {
    const fetchDataAndOpenWindow = async () => {
      try {
        const { data: exportData } = await refetchData();
        window.open(exportData?.GenerateReportNoQueue || "", "_blank");
        enqueueSnackbar(`${t("button.export")}สำเร็จ`, {
          variant: "success",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        enqueueSnackbar(`${t("button.export")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    };

    if (exportInput !== null) {
      fetchDataAndOpenWindow();
    }
  }, [enqueueSnackbar, exportInput, refetchData, t]);

  if (exportInput && isExporting) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">
          {t("contact.contact_type.customer")}
        </Typography>
        <Stack direction={"row"} gap={1}>
          <CustomizedButton
            title={`${t("button.add")}${t("contact.contact_type.customer")}`}
            variant="contained"
            onClick={() => navigate(`${pathname}/add?tab=address`)}
            addIcon
          />
          <CustomizedMenuOptions
            isIcon
            options={customerOptions}
            onSelect={(e) => {
              const value = e.target as HTMLElement;
              switch (value.innerText) {
                case `${t("button.import")}`:
                  navigate(`${pathname}/import`);
                  break;
                case `${t("button.export")}`:
                  openModalHandler();
                  break;
                default:
              }
            }}
          />
        </Stack>
      </Box>
      <ContactTable type="customer" />
      <ExporterModal
        open={modal}
        closeModalHandler={closeModalHandler}
        typeOptions={customerTypeOptions}
        exportHandler={exportHandler}
      />
    </>
  );
};

export default Customer;
